import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence } from 'motion/react'
import { useLaggedValue } from 'hooks/useLaggedValue'
import { type DiscountStepViewProps } from '../../discountProgressProps'
import {
  type DiscountProgressVariant,
  INSTANT_TRANSITION,
  LEG_DURATION,
  LEG_TRANSITION,
  PROGRESS_TRANSITION,
} from './discountProgress.consts'
import { Marker } from './Marker'
import * as S from './DiscountProgressBar.styles'

type Props = {
  steps: DiscountStepViewProps[]
  progress: number
  reduceMotion: boolean
  variant?: DiscountProgressVariant
}

const countReached = (pattern: string) =>
  [...pattern].filter((c) => c === '1').length

function getNextPattern(current: string, target: string): string {
  if (current.length !== target.length) return target

  const changed = [...target].flatMap((reached, index) =>
    reached === current[index] ? [] : index
  )
  if (changed.length === 0) return target

  const unlocking = countReached(target) > countReached(current)
  const next = unlocking ? changed[0] : changed[changed.length - 1]

  return current.slice(0, next) + target[next] + current.slice(next + 1)
}

function useSteppedPattern(target: string, legMs: number): string {
  const [walk, setWalk] = useState({ target, pattern: target })

  const pattern =
    walk.target === target || legMs === 0
      ? walk.pattern
      : getNextPattern(walk.pattern, target)

  useEffect(() => {
    if (legMs === 0) {
      if (walk.pattern !== target || walk.target !== target) {
        setWalk({ target, pattern: target })
      }
      return
    }
    if (walk.target !== target) {
      setWalk({ target, pattern })
      return
    }
    if (walk.pattern === target) return
    const timer = setTimeout(
      () => setWalk({ target, pattern: getNextPattern(walk.pattern, target) }),
      legMs
    )
    return () => clearTimeout(timer)
  }, [walk, target, pattern, legMs])

  return pattern
}

// Resizes are held while walking so mid-leg re-measures can't restart the fill.
function useFillCap(reorderKey: string, holdResizes: boolean) {
  const capRef = useRef<HTMLDivElement>(null)
  const [caps, setCaps] = useState({ start: 0, cap: 0, prevCap: 0 })

  const measure = (reordered: boolean) => {
    const el = capRef.current
    if (!el) return
    const nextStart = el.offsetLeft
    const nextCap = nextStart + el.offsetWidth
    setCaps((c) =>
      c.start === nextStart && c.cap === nextCap
        ? c
        : {
            start: nextStart,
            cap: nextCap,
            prevCap: reordered ? c.cap : c.prevCap,
          }
    )
  }

  useLayoutEffect(() => measure(true), [reorderKey])

  const holdResizesRef = useRef(holdResizes)
  holdResizesRef.current = holdResizes

  useEffect(() => {
    if (!holdResizes) measure(false)
  }, [holdResizes])

  useEffect(() => {
    const el = capRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      if (!holdResizesRef.current) measure(false)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return { capRef, ...caps }
}

export function DiscountProgressBar({
  steps,
  progress,
  reduceMotion,
  variant = 'tinted-pill',
}: Props) {
  const stepsPattern = steps.map((step) => (step.applied ? '1' : '0')).join('')
  const legMs = reduceMotion ? 0 : LEG_DURATION * 1000

  const pattern = useSteppedPattern(stepsPattern, legMs)
  const laggedPattern = useLaggedValue(pattern, legMs)
  const settledPattern =
    laggedPattern.length === pattern.length ? laggedPattern : pattern
  const walking = pattern !== stepsPattern || pattern !== settledPattern
  const unlocking = countReached(stepsPattern) > countReached(settledPattern)

  const { capRef, start, cap, prevCap } = useFillCap(pattern, walking)

  // 0% sits at the reached chips' right edge, not under them.
  const toVisibleTrackPct = (progressPct: number) =>
    cap === 0
      ? progressPct
      : ((start + (progressPct / 100) * (cap - start)) / cap) * 100

  // prevCap is the fill's approach target: the DOM reorders at leg start while
  // the marker's slide is still held back by its transition delay.
  const approachPct = cap === 0 ? 0 : (prevCap / cap) * 100
  const legEnd =
    pattern === stepsPattern
      ? toVisibleTrackPct(progress)
      : unlocking
      ? toVisibleTrackPct(0)
      : 100
  const legKeyframes: [null, string, string] = unlocking
    ? [null, `${approachPct}%`, `${legEnd}%`]
    : [null, `${toVisibleTrackPct(0)}%`, `${legEnd}%`]

  return (
    <S.BarRow data-testid="discount-progress-bar" $variant={variant}>
      <S.Track />
      <S.FillTrack style={{ width: cap }}>
        <S.Fill
          initial={false}
          animate={
            walking
              ? { width: legKeyframes }
              : { width: `${toVisibleTrackPct(progress)}%` }
          }
          transition={
            reduceMotion
              ? INSTANT_TRANSITION
              : walking
              ? LEG_TRANSITION
              : PROGRESS_TRANSITION
          }
        />
      </S.FillTrack>
      <S.FillCap ref={capRef} />
      <AnimatePresence initial={false} mode="popLayout">
        {steps.map((step, index) => (
          <Marker
            key={index}
            step={step}
            reached={pattern[index] === '1'}
            reduceMotion={reduceMotion}
          />
        ))}
      </AnimatePresence>
    </S.BarRow>
  )
}
